import React, { useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@/shared/ui';
import { createAppTheme } from '@/shared/theme';
import loginBackground from '../../../../assets/background-login.jpg';
import clinicLogo from '../../../../assets/logo-nha-khoa.png';

/** Login / forgot-password fields — slightly taller than global form.controlHeight. */
export const authFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 44,
  },
  '& .MuiOutlinedInput-input': {
    pl: 0,
  },
} as const;

/** Extra height so the content block can sit on the vertical center of the card. */
export const AUTH_CARD_MIN_HEIGHT = 460;

type AuthPageShellProps = {
  children: React.ReactNode;
};

/**
 * Shared unsigned-user chrome: photo, dark overlay, corner logo, centered white card.
 */
const AuthPageShell: React.FC<AuthPageShellProps> = ({ children }) => {
  const { t } = useTranslation('identity');
  const cardTheme = useMemo(() => createAppTheme('light'), []);

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        pt: 3,
        pb: 3,
        backgroundImage: `url(${loginBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'rgba(0, 0, 0, 0.45)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          zIndex: 2,
          top: 24,
          left: 24,
          bgcolor: '#fff',
          borderRadius: 2,
          boxShadow: 2,
          px: 1.5,
          py: 1,
        }}
      >
        <Box
          component="img"
          src={clinicLogo}
          alt={t('brand')}
          sx={{
            display: 'block',
            height: 72,
            width: 'auto',
          }}
        />
      </Box>
      <ThemeProvider theme={cardTheme}>
        <Stack
          spacing={2}
          alignItems="center"
          sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 420 }}
        >
          <Box
            sx={{
              width: '100%',
              minHeight: AUTH_CARD_MIN_HEIGHT,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              bgcolor: '#fff',
              color: 'text.primary',
              borderRadius: 4,
              boxShadow: 2,
              px: { xs: 3, sm: 4 },
              py: { xs: 4, sm: 5 },
            }}
          >
            {children}
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.78)' }}>
            {t('copyright', { year: new Date().getFullYear() })}
          </Typography>
        </Stack>
      </ThemeProvider>
    </Box>
  );
};

export default AuthPageShell;
